import moment from "moment";

export { Time };

// If undefined will use machines utc
function _createMomentUTC(date, UTCOffset = undefined) {
  let m = moment(date);
  if (UTCOffset !== undefined) {
    m.utcOffset(UTCOffset);
  }
  return m;
}

function capitalize(string) {
  return string.charAt(0).toUpperCase() + string.slice(1);
}
function throwIfUnitInvalid(unit, validUnits) {
  if (!validUnits.includes(unit))
    throw new Error(
      `${unit} is not a valid unit. Expect one of: ${validUnits.toString()}`
    );
}

const Time = {
  unit: {
    millisecond: "millisecond",
    second: "second",
    minute: "minute",
    hour: "hour",
    day: "day",
    week: "isoWeek",
    month: "month",
    year: "year"
  },
  date: {
    create(args) {
      for (let unit in args) {
        throwIfUnitInvalid(unit, DATE_VALID_UNITS);
      }
      // Months index start with 0
      // Feb needs to be 1
      if (args.month !== undefined) {
        args.month -= 1;
      }
      return moment(args).toDate();
    },
    monthString(date, UTCOffset = undefined) {
      let m = _createMomentUTC(date, UTCOffset);
      return m.format("MMMM");
    },
    dayString(date, UTCOffset = undefined) {
      let m = _createMomentUTC(date, UTCOffset);
      return m.format("Do");
    },
    yearString(date, UTCOffset = undefined) {
      let m = _createMomentUTC(date, UTCOffset);
      return m.format("YYYY");
    },
    get(date, unit, UTCOffset = undefined) {
      throwIfUnitInvalid(unit, DATE_VALID_UNITS);
      let m = _createMomentUTC(date, UTCOffset);
      let result = m.get(unit);
      if (unit === Time.unit.month) {
        result += 1;
      }
      return result;
    },
    getAll(date, UTCOffset = undefined) {
      let m = _createMomentUTC(date, UTCOffset);
      let result = m.toObject();
      result.months += 1;
      let finalResult = {
        [Time.unit.year]: result.years,
        [Time.unit.month]: result.months,
        [Time.unit.day]: result.date,
        [Time.unit.hour]: result.hours,
        [Time.unit.minute]: result.minutes,
        [Time.unit.second]: result.seconds,
        [Time.unit.millisecond]: result.milliseconds
      };
      return finalResult;
    },
    startOf(unit, UTCOffset = undefined) {
      let m = _createMomentUTC(Time.date.getNow(), UTCOffset);
      return m.startOf(unit).toDate();
    },
    getUTCOffSet() {
      // Real offset is negative of what this function returns.
      // California is -8, which should be -480 in minutes here.
      // getTimezoneOffset() will return 480.
      return -new Date().getTimezoneOffset();
    },
    clone(date) {
      return new Date(date.getTime());
    },
    getNow() {
      return moment().toDate();
    },
    subtract(date, number, unit) {
      return moment(date)
        .subtract(number, unit)
        .toDate();
    },
    add(date, number, unit) {
      return moment(date)
        .add(number, unit)
        .toDate();
    },
    isSame(date1, UTCOffset1, date2, UTCOffset2, unit) {
      throwIfUnitInvalid(unit, [
        Time.unit.day,
        Time.unit.month,
        Time.unit.week
      ]);

      if (unit === Time.unit.day) {
        let d1 = Time.date.get(date1, Time.unit.day, UTCOffset1);
        let m1 = Time.date.get(date1, Time.unit.month, UTCOffset1);
        let y1 = Time.date.get(date1, Time.unit.year, UTCOffset1);
        let d2 = Time.date.get(date2, Time.unit.day, UTCOffset2);
        let m2 = Time.date.get(date2, Time.unit.month, UTCOffset2);
        let y2 = Time.date.get(date2, Time.unit.year, UTCOffset2);
        return d1 === d2 && m1 === m2 && y1 === y2;
      } else if (unit === Time.unit.week) {
        // TODO: make Time.date.get handle week
        let m1 = _createMomentUTC(date1, UTCOffset1);
        let m2 = _createMomentUTC(date2, UTCOffset2);
        let w1 = m1.isoWeek();
        let y1 = m1.isoWeekYear();
        let w2 = m2.isoWeek();
        let y2 = m2.isoWeekYear();
        return w1 === w2 && y1 === y2;
      } else if (unit === Time.unit.month) {
        let m1 = Time.date.get(date1, Time.unit.month, UTCOffset1);
        let y1 = Time.date.get(date1, Time.unit.year, UTCOffset1);
        let m2 = Time.date.get(date2, Time.unit.month, UTCOffset2);
        let y2 = Time.date.get(date2, Time.unit.year, UTCOffset2);
        return m1 === m2 && y1 === y2;
      }
    },

    getStartOf(date, unit, UTCOffset = undefined) {
      throwIfUnitInvalid(unit, [
        Time.unit.day,
        Time.unit.month,
        Time.unit.week
      ]);

      let m = _createMomentUTC(date, UTCOffset);
      return m.startOf(unit);
    },

    getEndOf(date, unit, UTCOffset = undefined) {
      throwIfUnitInvalid(unit, [
        Time.unit.day,
        Time.unit.month,
        Time.unit.week
      ]);

      let m = _createMomentUTC(date, UTCOffset);
      return m.endOf(unit);
    },

    diffBlock(
      date1,
      UTCOffset1 = undefined,
      date2,
      UTCOffset2 = undefined,
      unit
    ) {
      let unitCapitalized = capitalize(unit);
      return this[`diff${unitCapitalized}Block`](
        date1,
        UTCOffset1,
        date2,
        UTCOffset2
      );
    },

    diffIsoWeekBlock(
      date1,
      UTCOffset1 = undefined,
      date2,
      UTCOffset2 = undefined
    ) {
      let m1 = _createMomentUTC(date1, UTCOffset1);
      let m2 = _createMomentUTC(date2, UTCOffset2);
      return Math.ceil(m1.startOf(Time.unit.week).diff(m2, "week", true));
    },
    diffMonthBlock(
      date1,
      UTCOffset1 = undefined,
      date2,
      UTCOffset2 = undefined
    ) {
      let year1 = Time.date.get(date1, Time.unit.year, UTCOffset1);
      let year2 = Time.date.get(date2, Time.unit.year, UTCOffset2);
      let month1 = Time.date.get(date1, Time.unit.month, UTCOffset1);
      let month2 = Time.date.get(date2, Time.unit.month, UTCOffset2);
      return month1 - month2 + (year1 - year2) * 12;
    },
    diffDayBlock(date1, UTCOffset1 = undefined, date2, UTCOffset2 = undefined) {
      let m1 = _createMomentUTC(date1, UTCOffset1);
      let m2 = _createMomentUTC(date2, UTCOffset2);
      return Math.ceil(m1.startOf(Time.unit.day).diff(m2, Time.unit.day, true));
    },
    firebaseTimestampToDate(timestamp) {
      if (
        timestamp !== undefined &&
        timestamp !== null &&
        typeof timestamp.toDate === "function"
      ) {
        return timestamp.toDate();
      }
      return timestamp;
    },
    firebaseTimestampsToDate(data) {
      let d = {};
      for (let key in data) {
        d[key] = Time.date.firebaseTimestampToDate(data[key]);
      }
      return d;
    }
  },
  duration: {
    create(args) {
      for (let unit in args) {
        throwIfUnitInvalid(unit, DURATION_VALID_UNITS);
      }
      return moment.duration(args).asMilliseconds();
    },
    diff(date1, date2) {
      return moment(date1).diff(moment(date2));
    },
    get(duration, unit) {
      return moment.duration(duration).get(unit);
    },
    as(duration, unit, isFloored = false) {
      throwIfUnitInvalid(unit, DURATION_VALID_UNITS);
      const unitCapitalized = capitalize(unit);
      let result = moment.duration(duration)[`as${unitCapitalized}s`]();
      if (isFloored) {
        result = Math.floor(result);
      }
      return result;
    }
  }
};

const DURATION_VALID_UNITS = [
  Time.unit.year,
  Time.unit.month,
  Time.unit.week,
  Time.unit.day,
  Time.unit.hour,
  Time.unit.minute,
  Time.unit.second,
  Time.unit.millisecond
];
const DATE_VALID_UNITS = [
  Time.unit.year,
  Time.unit.month,
  Time.unit.week,
  Time.unit.day,
  Time.unit.hour,
  Time.unit.minute,
  Time.unit.second,
  Time.unit.millisecond
];
